import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

const TOKEN_KEY = 'aletheias_auth_token'

export interface User {
  user_id: string
  tenant_id: string | null
  tenant_name: string | null
  tenant_type: 'customer' | 'labeling_admin' | string | null
  email: string
  first_name: string | null
  last_name: string | null
  role: 'admin' | 'vet' | 'tech' | 'viewer' | string
  products: string[]
  email_verified: boolean
  feature_channel: string
  features: Record<string, boolean>
  // 'fulfillment' for mail-order ("no hardware") tenants; null for hardware tenants.
  plan: string | null
  has_shipping_address: boolean
  show_no_engraver_wall: boolean
  // True until this user accepts the current agreement text.
  eula_acceptance_required: boolean
}

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(localStorage.getItem(TOKEN_KEY))
  const user = ref<User | null>(null)
  const loading = ref(false)

  const isAuthenticated = computed(() => !!user.value)
  const isAdmin = computed(
    () => user.value?.role === 'admin' || user.value?.tenant_type === 'labeling_admin'
  )
  const isLabelingAdmin = computed(() => user.value?.tenant_type === 'labeling_admin')
  const hasKeepsake = computed(() => user.value?.products?.includes('keepsake') ?? false)
  const fastProjectEnabled = computed(() => !!user.value?.features?.fast_project)
  const noseEnabled = computed(() => !!user.value?.features?.nose_print)
  // Face print (4th product): AI line-art portrait from a face photo. Gates
  // the product option on the intake screen. Internal-channel only until real
  // slate burns confirm the prompts.
  const facePrintEnabled = computed(() => !!user.value?.features?.face_print)
  // Selects which nose flow runs (deterministic triangle dither vs legacy AI
  // line art); nose as a product is still gated by noseEnabled above.
  const noseDitherEnabled = computed(() => !!user.value?.features?.nose_dither)
  // Pet ID tag designer (3rd product). Gates ALL staff-facing tag UI: the
  // submission queue, the nav badge, and the kiosk-QR settings panel. False for
  // normal stable-channel tenants, so the app is byte-identical for them.
  const tagDesignerEnabled = computed(() => !!user.value?.features?.tag_designer)
  // Logo engrave (5th product). Gates the dashboard button, the /new-logo
  // route, and the logo panel on the Account screen. Like the tag designer
  // this is a flow of its own rather than an option on the intake, because
  // there is no pet and no photo — the art is the clinic's stored logo.
  const logoEngraveEnabled = computed(() => !!user.value?.features?.logo_engrave)
  // Mail-order fulfillment ("no hardware"): the dark-launch feature gate...
  const mailOrderFulfillmentEnabled = computed(() => !!user.value?.features?.mail_order_fulfillment)
  // The per-tenant account type that actually drives the flow (fulfill
  // instead of engrave, no engraver wall, no tier picker).
  const isFulfillment = computed(() => user.value?.plan === 'fulfillment')
  const hasShippingAddress = computed(() => user.value?.has_shipping_address ?? false)
  const showNoEngraverWall = computed(() => user.value?.show_no_engraver_wall ?? false)
  const eulaAcceptanceRequired = computed(() => user.value?.eula_acceptance_required ?? false)
  const fullName = computed(() => {
    const u = user.value
    if (!u) return ''
    const first = u.first_name?.trim() || ''
    const last = u.last_name?.trim() || ''
    if (first && last) return `${first} ${last}`
    if (first) return first
    return u.email
  })
  const displayName = fullName // alias for components that read either name

  /**
   * Generic gate, for code that reads a feature key by name (the intake's
   * product registry does). The named computeds above stay for readability at
   * fixed call sites; both read the same map the gateway sends.
   */
  function hasFeature(key: string | undefined): boolean {
    if (!key) return true
    return !!user.value?.features?.[key]
  }

  function setToken(newToken: string | null) {
    token.value = newToken
    if (newToken) localStorage.setItem(TOKEN_KEY, newToken)
    else localStorage.removeItem(TOKEN_KEY)
  }

  async function fetchMe(): Promise<boolean> {
    if (!token.value) return false
    try {
      const resp = await fetch('/api/auth/me', {
        headers: { Authorization: `Bearer ${token.value}` },
      })
      if (!resp.ok) {
        clear()
        return false
      }
      user.value = (await resp.json()) as User
      return true
    } catch {
      clear()
      return false
    }
  }

  async function login(email: string, password: string): Promise<void> {
    loading.value = true
    try {
      const resp = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      })
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}))
        throw new Error(body.detail || `Login failed (${resp.status})`)
      }
      const data = (await resp.json()) as { access_token: string }
      setToken(data.access_token)
      const ok = await fetchMe()
      if (!ok) throw new Error('Logged in but failed to load profile')
    } finally {
      loading.value = false
    }
  }

  async function forgotPassword(email: string): Promise<void> {
    const resp = await fetch('/api/auth/forgot-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    })
    if (!resp.ok) {
      const body = await resp.json().catch(() => ({}))
      throw new Error(body.detail || `Request failed (${resp.status})`)
    }
  }

  async function resetPassword(resetToken: string, newPassword: string): Promise<void> {
    loading.value = true
    try {
      const resp = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken, password: newPassword }),
      })
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}))
        throw new Error(body.detail || `Reset failed (${resp.status})`)
      }
      const data = (await resp.json()) as { access_token: string }
      setToken(data.access_token)
      await fetchMe()
    } finally {
      loading.value = false
    }
  }

  function clear(): void {
    setToken(null)
    user.value = null
  }

  function logout(): void {
    clear()
    window.location.assign('/login')
  }

  return {
    token, user, loading,
    isAuthenticated, isAdmin, isLabelingAdmin, hasKeepsake, fastProjectEnabled, noseEnabled, noseDitherEnabled, facePrintEnabled, tagDesignerEnabled, logoEngraveEnabled, mailOrderFulfillmentEnabled, isFulfillment, hasShippingAddress, showNoEngraverWall, eulaAcceptanceRequired, fullName, displayName,
    hasFeature, fetchMe, login, forgotPassword, resetPassword, logout, clear, setToken,
  }
})
